import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Tooltip as ShadcnTooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line
} from 'recharts';
import {
    BarChart3, PieChart as PieIcon, Users, FileText, CheckCircle, XCircle,
    TrendingUp, Award, Info, Download, FileSpreadsheet, Calendar, Filter,
    HelpCircle, Sparkles
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    visible: { transition: { staggerChildren: 0.08 } }
};

// Tooltip explanations for all metrics
const METRIC_EXPLANATIONS = {
    total_mandates: {
        title: "Total Mandats",
        description: "Nombre total de mandats créés par votre agence sur la période sélectionnée.",
        tip: "Ce chiffre inclut tous les statuts : brouillons, en cours, validés et dénoncés."
    },
    validated: {
        title: "Mandats Validés",
        description: "Mandats qui ont été acceptés et signés par les propriétaires.",
        tip: "Un taux de validation élevé (>70%) indique une bonne qualification des prospects et une présentation efficace des mandats."
    },
    denounced: {
        title: "Mandats Dénoncés",
        description: "Mandats résiliés avant leur terme par le propriétaire ou l'agence.",
        tip: "Analysez les raisons des dénonciations pour améliorer la rétention : prix inadapté, délai trop long, communication insuffisante."
    },
    in_progress: {
        title: "En Cours",
        description: "Mandats envoyés aux propriétaires en attente de validation.",
        tip: "Un nombre élevé de mandats en attente peut indiquer un besoin de relance ou un problème de délivrabilité des emails."
    },
    type_distribution: {
        title: "Répartition par Type",
        description: "Distribution des mandats selon leur exclusivité.",
        tip: "Les mandats exclusifs génèrent généralement de meilleurs résultats car vous êtes le seul intermédiaire. Visez >50% d'exclusivité."
    },
    status_distribution: {
        title: "Répartition par Statut",
        description: "Vue d'ensemble du cycle de vie de vos mandats.",
        tip: "Un pipeline équilibré montre des mandats à tous les stades. Trop de brouillons = processus à fluidifier."
    },
    validated_by_type: {
        title: "Validations par Type",
        description: "Quel type de mandat obtient le plus de signatures ?",
        tip: "Comparez les taux de validation par type pour ajuster votre argumentaire commercial."
    },
    denounced_by_type: {
        title: "Dénonciations par Type",
        description: "Quel type de mandat est le plus souvent résilié ?",
        tip: "Si un type a plus de dénonciations, revoyez les conditions proposées ou la durée du mandat."
    },
    agent_performance: {
        title: "Performance par Agent",
        description: "Comparaison des résultats de chaque agent commercial.",
        tip: "Utilisez ces données pour identifier les bonnes pratiques des meilleurs agents et former l'équipe."
    },
    success_rate: {
        title: "Taux de Succès",
        description: "Pourcentage de mandats validés par rapport au total.",
        tip: "Benchmark secteur : 60-75%. Au-dessus = excellente performance. En dessous = axes d'amélioration à identifier."
    }
};

// Info tooltip component
function InfoTooltip({ metric }) {
    const info = METRIC_EXPLANATIONS[metric];
    if (!info) return null;
    
    return (
        <TooltipProvider>
            <ShadcnTooltip delayDuration={200}>
                <TooltipTrigger asChild>
                    <button className="ml-2 p-1 rounded-full hover:bg-muted transition-colors">
                        <Info className="w-4 h-4 text-muted-foreground hover:text-muted-foreground" />
                    </button>
                </TooltipTrigger>
                <TooltipContent 
                    side="top" 
                    className="max-w-xs bg-slate-800 border-border p-4"
                >
                    <div className="space-y-2">
                        <p className="font-semibold text-white">{info.title}</p>
                        <p className="text-sm text-muted-foreground">{info.description}</p>
                        <div className="pt-2 border-t border-border">
                            <p className="text-xs text-[#FF914D] flex items-start gap-1">
                                <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {info.tip}
                            </p>
                        </div>
                    </div>
                </TooltipContent>
            </ShadcnTooltip>
        </TooltipProvider>
    );
}

// Custom tooltip for charts
const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-800/95 backdrop-blur-sm border border-border rounded-xl px-4 py-3 shadow-2xl">
                <p className="font-semibold text-white mb-1">{payload[0].payload.name || label}</p>
                {payload.map((entry, index) => (
                    <p key={index} className="text-sm" style={{ color: entry.color }}>
                        {entry.name}: <span className="font-bold">{entry.value}</span>
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

// Render label with better styling
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }) => {
    if (percent < 0.08 || value === 0) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
        <text 
            x={x} 
            y={y} 
            fill="white" 
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="14"
            fontWeight="700"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
        >
            {value}
        </text>
    );
};

// Stat card with gradient and tooltip
function StatCard({ title, value, subtitle, icon: Icon, gradient, metric, trend }) {
    return (
        <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            transition={{ type: "spring", stiffness: 300 }}
            className={`bg-gradient-to-br ${gradient} rounded-2xl p-5 relative overflow-hidden shadow-lg`}
        >
            <div className="absolute top-0 right-0 w-32 h-32 bg-muted rounded-full -mr-12 -mt-12 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-black/10 rounded-full -ml-8 -mb-8" />
            <div className="relative">
                <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner">
                        <Icon className="w-6 h-6 text-white" />
                    </div>
                    <InfoTooltip metric={metric} />
                </div>
                <p className="text-4xl font-black text-white tracking-tight">{value}</p>
                <p className="text-sm font-medium text-white/90 mt-1">{title}</p>
                {subtitle && (
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                        {trend && <TrendingUp className="w-3 h-3" />}
                        {subtitle}
                    </p>
                )}
            </div>
        </motion.div>
    );
}

// Modern pie chart card
function ModernPieChart({ title, data, icon: Icon, metric, colors }) {
    const hasData = data && data.some(d => d.value > 0);
    const total = data?.reduce((sum, d) => sum + d.value, 0) || 0;
    
    return (
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-border overflow-hidden">
            <CardHeader className="pb-2">
                <CardTitle className="text-base text-white flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#681793]/30 to-[#FF914D]/30 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#FF914D]" />
                    </div>
                    {title}
                    <InfoTooltip metric={metric} />
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {hasData ? (
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-40 flex-shrink-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={data.filter(d => d.value > 0)}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={35}
                                        outerRadius={65}
                                        paddingAngle={3}
                                        dataKey="value"
                                        label={renderCustomLabel}
                                        labelLine={false}
                                    >
                                        {data.map((entry, index) => (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={entry.color}
                                                stroke="rgba(0,0,0,0.2)"
                                                strokeWidth={1}
                                            />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip content={<CustomChartTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex-1 space-y-2">
                            {data.filter(d => d.value > 0).map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div 
                                            className="w-3 h-3 rounded-full" 
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className="text-sm text-muted-foreground">{item.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-white">{item.value}</span>
                                        <span className="text-xs text-muted-foreground">
                                            ({total > 0 ? Math.round((item.value / total) * 100) : 0}%)
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="h-40 flex items-center justify-center">
                        <p className="text-white/30 text-sm">Pas de données</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// Agent performance section
function AgentPerformanceSection({ agents }) {
    if (!agents || agents.length === 0) {
        return (
            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-border">
                <CardHeader>
                    <CardTitle className="text-base text-white flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#681793]/30 to-[#FF914D]/30 flex items-center justify-center">
                            <Users className="w-4 h-4 text-[#FF914D]" />
                        </div>
                        Performance par Agent
                        <InfoTooltip metric="agent_performance" />
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-white/30 text-center py-8">Aucune donnée disponible</p>
                </CardContent>
            </Card>
        );
    }
    
    const chartData = agents.map(agent => ({
        name: agent.agent_name.split(' ')[0],
        fullName: agent.agent_name,
        Total: agent.total,
        Validés: agent.validated,
        Dénoncés: agent.denounced
    }));
    
    return (
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-border">
            <CardHeader>
                <CardTitle className="text-base text-white flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#681793]/30 to-[#FF914D]/30 flex items-center justify-center">
                        <Users className="w-4 h-4 text-[#FF914D]" />
                    </div>
                    Performance par Agent
                    <InfoTooltip metric="agent_performance" />
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="h-48 mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical" barGap={4}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                            <XAxis type="number" stroke="rgba(255,255,255,0.3)" fontSize={12} />
                            <YAxis 
                                dataKey="name" 
                                type="category" 
                                stroke="rgba(255,255,255,0.3)" 
                                width={70} 
                                fontSize={12}
                            />
                            <RechartsTooltip content={<CustomChartTooltip />} />
                            <Bar dataKey="Total" fill="#3B82F6" radius={[0, 4, 4, 0]} barSize={12} />
                            <Bar dataKey="Validés" fill="#22C55E" radius={[0, 4, 4, 0]} barSize={12} />
                            <Bar dataKey="Dénoncés" fill="#EF4444" radius={[0, 4, 4, 0]} barSize={12} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                
                {/* Agent cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {agents.map((agent, idx) => {
                        const successRate = agent.total > 0 ? Math.round((agent.validated / agent.total) * 100) : 0;
                        return (
                            <motion.div 
                                key={idx}
                                whileHover={{ scale: 1.01 }}
                                className="flex items-center justify-between p-4 rounded-xl bg-card hover:bg-muted border border-border hover:border-border transition-all"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold text-sm shadow-lg">
                                        {agent.agent_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                    </div>
                                    <div>
                                        <p className="font-medium text-white text-sm">{agent.agent_name}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
                                                {agent.by_type?.simple || 0} simple
                                            </span>
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">
                                                {agent.by_type?.exclusif || 0} excl.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-center">
                                        <p className="text-lg font-bold text-white">{agent.total}</p>
                                        <p className="text-[10px] text-muted-foreground">Total</p>
                                    </div>
                                    <div className={`text-center px-2 py-1 rounded-lg ${successRate >= 70 ? 'bg-green-500/20' : successRate >= 50 ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                                        <p className={`text-lg font-bold ${successRate >= 70 ? 'text-green-400' : successRate >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                                            {successRate}%
                                        </p>
                                        <p className="text-[10px] text-muted-foreground">Succès</p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}

// Period options
const PERIOD_OPTIONS = [
    { value: 'week', label: 'Cette semaine' },
    { value: 'month', label: 'Ce mois' },
    { value: 'quarter', label: 'Ce trimestre' },
    { value: 'year', label: 'Cette année' },
    { value: 'all', label: 'Tout' }
];

export default function MandateStatsPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('all');
    const [exporting, setExporting] = useState(false);
    const statsRef = useRef(null);

    const loadStats = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/mandates-v2/stats', { params: { period } });
            setStats(response.data);
        } catch (err) {
            console.error('Error loading stats:', err);
            toast.error('Erreur lors du chargement des statistiques');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const exportToExcel = () => {
        if (!stats) return;
        
        setExporting(true);
        try {
            // Prepare data sheets
            const summaryData = [
                ['Statistiques des Mandats', '', ''],
                ['Période', PERIOD_OPTIONS.find(p => p.value === period)?.label, ''],
                ['', '', ''],
                ['Métrique', 'Valeur', 'Description'],
                ['Total mandats', stats.total_mandates, 'Nombre total de mandats'],
                ['Mandats validés', stats.by_status?.acknowledged || 0, 'Mandats signés par les propriétaires'],
                ['Mandats dénoncés', stats.by_status?.denounced || 0, 'Mandats résiliés'],
                ['En cours', (stats.by_status?.sent || 0) + (stats.by_status?.generated || 0), 'En attente de validation'],
                ['Taux de succès', `${stats.total_mandates > 0 ? ((stats.by_status?.acknowledged / stats.total_mandates) * 100).toFixed(1) : 0}%`, 'Pourcentage de validation'],
            ];

            const typeData = [
                ['Type de Mandat', 'Total', 'Validés', 'Dénoncés'],
                ['Simple', stats.by_type?.simple?.count || 0, stats.by_type?.simple?.validated || 0, stats.by_type?.simple?.denounced || 0],
                ['Exclusif', stats.by_type?.exclusif?.count || 0, stats.by_type?.exclusif?.validated || 0, stats.by_type?.exclusif?.denounced || 0],
                ['Semi-exclusif', stats.by_type?.semi_exclusif?.count || 0, stats.by_type?.semi_exclusif?.validated || 0, stats.by_type?.semi_exclusif?.denounced || 0],
            ];

            const agentData = [
                ['Agent', 'Total', 'Validés', 'Dénoncés', 'Taux succès', 'Simple', 'Exclusif', 'Semi-excl.'],
                ...(stats.by_agent || []).map(a => [
                    a.agent_name,
                    a.total,
                    a.validated,
                    a.denounced,
                    `${a.total > 0 ? Math.round((a.validated / a.total) * 100) : 0}%`,
                    a.by_type?.simple || 0,
                    a.by_type?.exclusif || 0,
                    a.by_type?.semi_exclusif || 0
                ])
            ];

            const wb = XLSX.utils.book_new();
            
            const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
            ws1['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 40 }];
            XLSX.utils.book_append_sheet(wb, ws1, 'Résumé');
            
            const ws2 = XLSX.utils.aoa_to_sheet(typeData);
            ws2['!cols'] = [{ wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
            XLSX.utils.book_append_sheet(wb, ws2, 'Par Type');
            
            const ws3 = XLSX.utils.aoa_to_sheet(agentData);
            ws3['!cols'] = [{ wch: 25 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 8 }, { wch: 10 }];
            XLSX.utils.book_append_sheet(wb, ws3, 'Par Agent');

            const periodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || 'all';
            XLSX.writeFile(wb, `Stats_Mandats_${periodLabel.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
            
            toast.success('Export Excel téléchargé');
        } catch (err) {
            console.error('Export error:', err);
            toast.error('Erreur lors de l\'export');
        } finally {
            setExporting(false);
        }
    };

    const exportToPDF = async () => {
        if (!statsRef.current || !stats) return;
        
        setExporting(true);
        try {
            const canvas = await html2canvas(statsRef.current, {
                backgroundColor: '#0f172a',
                scale: 2,
                useCORS: true,
                logging: false
            });
            
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });
            
            const imgWidth = 287;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            // Add header
            pdf.setFillColor(104, 23, 147);
            pdf.rect(0, 0, 297, 20, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(16);
            pdf.text('Homly - Statistiques des Mandats', 10, 13);
            pdf.setFontSize(10);
            const periodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || 'Tout';
            pdf.text(`Période: ${periodLabel} | Généré le: ${new Date().toLocaleDateString('fr-FR')}`, 200, 13);
            
            // Add screenshot
            pdf.addImage(imgData, 'PNG', 5, 25, imgWidth, imgHeight);
            
            pdf.save(`Stats_Mandats_${periodLabel.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
            
            toast.success('Export PDF téléchargé');
        } catch (err) {
            console.error('PDF export error:', err);
            toast.error('Erreur lors de l\'export PDF');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <Skeleton key={i} className="h-36 bg-card rounded-2xl" />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {[1, 2, 3, 4].map(i => (
                        <Skeleton key={i} className="h-64 bg-card rounded-2xl" />
                    ))}
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="p-6 text-center py-20">
                <BarChart3 className="w-16 h-16 mx-auto text-white/20 mb-4" />
                <p className="text-muted-foreground">Impossible de charger les statistiques</p>
                <Button onClick={loadStats} variant="outline" className="mt-4 border-border text-muted-foreground">
                    Réessayer
                </Button>
            </div>
        );
    }

    const totalValidated = stats.by_status?.acknowledged || 0;
    const totalDenounced = stats.by_status?.denounced || 0;
    const inProgress = (stats.by_status?.sent || 0) + (stats.by_status?.generated || 0);
    const successRate = stats.total_mandates > 0 
        ? ((totalValidated / stats.total_mandates) * 100).toFixed(1) 
        : 0;

    return (
        <motion.div 
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="p-6 space-y-6"
        >
            {/* Header with controls */}
            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-white flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shadow-lg">
                            <BarChart3 className="w-5 h-5 text-white" />
                        </div>
                        Statistiques des Mandats
                    </h1>
                    <p className="text-muted-foreground mt-1 ml-14">Vue d'ensemble de l'activité de votre agence</p>
                </div>
                
                <div className="flex items-center gap-3">
                    {/* Period filter */}
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-40 bg-card border-border text-white">
                            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-border">
                            {PERIOD_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    {/* Export buttons */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={exportToExcel}
                        disabled={exporting}
                        className="border-border text-muted-foreground hover:bg-card"
                    >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Excel
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={exportToPDF}
                        disabled={exporting}
                        className="border-border text-muted-foreground hover:bg-card"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                    </Button>
                </div>
            </motion.div>

            {/* Stats content (for PDF export) */}
            <div ref={statsRef}>
                {/* KPI Cards */}
                <motion.div variants={fadeIn} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <StatCard 
                        title="Total mandats"
                        value={stats.total_mandates}
                        icon={FileText}
                        gradient="from-[#681793] to-[#9b4dca]"
                        metric="total_mandates"
                    />
                    <StatCard 
                        title="Mandats validés"
                        value={totalValidated}
                        subtitle={`${successRate}% de succès`}
                        icon={CheckCircle}
                        gradient="from-emerald-600 to-emerald-400"
                        metric="validated"
                        trend
                    />
                    <StatCard 
                        title="Mandats dénoncés"
                        value={totalDenounced}
                        icon={XCircle}
                        gradient="from-red-600 to-red-400"
                        metric="denounced"
                    />
                    <StatCard 
                        title="En cours"
                        value={inProgress}
                        subtitle="Attente de validation"
                        icon={TrendingUp}
                        gradient="from-[#FF914D] to-[#ffb380]"
                        metric="in_progress"
                    />
                </motion.div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <motion.div variants={fadeIn}>
                        <ModernPieChart 
                            title="Répartition par type"
                            data={stats.charts?.type_distribution}
                            icon={PieIcon}
                            metric="type_distribution"
                        />
                    </motion.div>
                    
                    <motion.div variants={fadeIn}>
                        <ModernPieChart 
                            title="Répartition par statut"
                            data={stats.charts?.status_distribution?.filter(d => d.value > 0)}
                            icon={PieIcon}
                            metric="status_distribution"
                        />
                    </motion.div>
                    
                    <motion.div variants={fadeIn}>
                        <ModernPieChart 
                            title="Mandats validés par type"
                            data={stats.charts?.validated_by_type}
                            icon={CheckCircle}
                            metric="validated_by_type"
                        />
                    </motion.div>
                    
                    <motion.div variants={fadeIn}>
                        <ModernPieChart 
                            title="Mandats dénoncés par type"
                            data={stats.charts?.denounced_by_type}
                            icon={XCircle}
                            metric="denounced_by_type"
                        />
                    </motion.div>
                </div>

                {/* Agent Performance */}
                <motion.div variants={fadeIn}>
                    <AgentPerformanceSection agents={stats.by_agent} />
                </motion.div>
            </div>
        </motion.div>
    );
}
